import { useState, useRef, useEffect } from 'react'
import { asistenteApi } from '../api/asistente'
import Boton from './Boton'

// Extrae el texto legible de un content (string o array de bloques Anthropic)
function textoDe(content) {
  if (typeof content === 'string') return content
  if (Array.isArray(content)) {
    return content.filter(b => b.type === 'text').map(b => b.text).join('\n')
  }
  return ''
}

// Menú de acciones del asistente, agrupado por área. Cada botón COMPLETA la
// caja con una plantilla para llenar — no manda nada: cada mensaje al bot
// tiene costo y un "Quiero cambiar la dirección…" a secas solo servía para que
// el bot preguntara "¿cuál?" (Martin, 11 oct 2026).
const MENU = [
  {
    titulo: 'Consultas y mejoras',
    items: [
      { label: '❓ Tengo una duda de cómo hacer algo', plantilla: 'Tengo una duda: ' },
      { label: '💡 Proponer una mejora del sistema', plantilla: 'Propongo esta mejora (qué cambiarías, en qué pantalla y por qué): ' },
    ],
  },
  {
    titulo: 'Logística — documentos',
    items: [
      { label: '📍 Cambiar dirección de envío', plantilla: 'Cambiar la dirección de envío del remito / nota de envío Nº \nDomicilio (calle, número, piso, depto): \nLocalidad: \nProvincia: \nCP: \nTeléfono: \nRecibe: ' },
      { label: '📅 Cambiar fecha/horario de entrega', plantilla: 'Cambiar la entrega del remito / nota de envío Nº \nFecha: \nHorario: ' },
    ],
  },
]

export default function AsistenteChat() {
  const [abierto, setAbierto] = useState(false)
  const [mensajes, setMensajes] = useState([]) // historial Anthropic [{role, content}] (lo guarda el servidor)
  const [conversacionId, setConversacionId] = useState(null)
  // Acción que espera el ✅ de la persona: { codigo, accion }
  const [pendiente, setPendiente] = useState(null)
  const [input, setInput] = useState('')
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState('')
  // Capturas adjuntas al próximo mensaje (sug #54): [{token, nombre}].
  const [adjuntos, setAdjuntos] = useState([])
  const [subiendo, setSubiendo] = useState(false)
  const scrollRef = useRef(null)
  const inputRef = useRef(null)
  const fileRef = useRef(null)

  // Mensajes visibles (solo texto de user/assistant; oculta los tool_result internos).
  // Los marcadores de adjuntos se muestran como "📎 captura adjunta".
  const visibles = mensajes
    .map(m => ({ role: m.role, texto: textoDe(m.content).replace(/\[imagen adjunta: [^\]]+\]/g, '📎 captura adjunta') }))
    // El ✅ viaja al asistente con el resultado real de la acción: acá se ve solo el tilde
    .map(m => m.role === 'user' && m.texto.startsWith('✅ Confirmé con el botón') ? { ...m, texto: '✅ Confirmado' } : m)
    .filter(m => m.texto.trim() !== '')

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [visibles.length, cargando])

  useEffect(() => {
    if (abierto) setTimeout(() => inputRef.current?.focus(), 100)
  }, [abierto])

  // Pone la plantilla en la caja con el cursor después del "Nº " para que la
  // persona complete lo que falta y recién ahí la mande.
  const usarPlantilla = (plantilla) => {
    setInput(plantilla)
    setTimeout(() => {
      const el = inputRef.current
      if (!el) return
      el.style.height = 'auto'
      el.style.height = Math.min(el.scrollHeight, 120) + 'px'
      el.focus()
      const pos = plantilla.includes('Nº ') ? plantilla.indexOf('Nº ') + 3 : plantilla.length
      el.setSelectionRange(pos, pos)
      el.scrollTop = 0 // que se vea el renglón del Nº, lo primero a completar
    }, 0)
  }

  const enviar = async (texto) => {
    let msg = (texto ?? input).trim()
    if ((!msg && adjuntos.length === 0) || cargando) return
    // Las capturas viajan como marcadores en el texto: el modelo los pasa a
    // registrar_sugerencia (parámetro adjuntos) y la imagen queda guardada.
    if (adjuntos.length > 0) {
      msg = (msg || 'Adjunto captura de pantalla.') +
        adjuntos.map(a => `\n[imagen adjunta: ${a.token}]`).join('')
    }
    setError('')
    setInput('')
    setAdjuntos([])
    if (inputRef.current) inputRef.current.style.height = 'auto'
    const nuevoHistorial = [...mensajes, { role: 'user', content: msg }]
    setMensajes(nuevoHistorial)
    setPendiente(null)
    setCargando(true)
    try {
      const { data } = await asistenteApi.chat(conversacionId, msg)
      aplicar(data, nuevoHistorial)
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo contactar al asistente.')
      // revertir el historial al estado previo al envío fallido
      setMensajes(mensajes)
      setInput(msg)
    } finally {
      setCargando(false)
    }
  }

  const aplicar = (data, respaldo) => {
    setConversacionId(data.conversacion_id ?? conversacionId)
    setMensajes(data.mensajes || respaldo)
    setPendiente(data.confirmacion || null)
    if (data.error) setError(data.respuesta || 'Error del asistente.')
  }

  // ✅ ejecuta ESA acción con los datos que se propusieron; ✕ la cancela.
  const resolver = async (confirmar) => {
    if (!pendiente || cargando) return
    const { codigo } = pendiente
    setPendiente(null)
    setError('')
    setCargando(true)
    try {
      const { data } = confirmar
        ? await asistenteApi.confirmar(conversacionId, codigo)
        : await asistenteApi.cancelar(conversacionId, codigo)
      aplicar(data, mensajes)
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo contactar al asistente.')
      setPendiente({ ...pendiente })
    } finally {
      setCargando(false)
    }
  }

  const adjuntar = async (file) => {
    if (!file) return
    setSubiendo(true); setError('')
    try {
      const { data } = await asistenteApi.adjunto(file)
      setAdjuntos(prev => [...prev, { token: data.token, nombre: data.nombre }])
    } catch (err) {
      const d = err.response?.data
      setError(d?.errors ? Object.values(d.errors).flat().join(' · ') : (d?.message || 'No se pudo subir la imagen.'))
    } finally {
      setSubiendo(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const reiniciar = () => { setMensajes([]); setConversacionId(null); setPendiente(null); setError(''); setAdjuntos([]) }

  return (
    <>
      {/* Botón flotante */}
      {!abierto && (
        <button onClick={() => setAbierto(true)} style={st.fab} title="Asistente">
          <span style={{ fontSize: '1.5rem' }}>💬</span>
        </button>
      )}

      {/* Panel */}
      {abierto && (
        <div style={st.panel}>
          <div style={st.header}>
            <div style={st.headerTitle}>
              <span style={{ fontSize: '1.1rem' }}>🤖</span>
              <div>
                <div style={st.headerName}>Asistente LGR</div>
                <div style={st.headerSub}>Tareas rápidas por chat</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {visibles.length > 0 && (
                <button onClick={reiniciar} style={st.iconBtn} title="Nueva conversación">↻</button>
              )}
              <button onClick={() => setAbierto(false)} style={st.iconBtn} title="Cerrar">✕</button>
            </div>
          </div>

          <div ref={scrollRef} style={st.body}>
            {visibles.length === 0 && (
              <div style={st.welcome}>
                <p style={{ fontSize: '2rem', margin: 0 }}>🤖</p>
                <p style={st.welcomeText}>Hola — soy el asistente de tu área. Pedime tareas, consultame dudas de uso, o contame qué mejorarías del sistema: lo registro y el equipo lo revisa.</p>
                <div style={st.menu}>
                  {MENU.map(grupo => (
                    <div key={grupo.titulo} style={st.menuGrupo}>
                      <p style={st.menuTitulo}>{grupo.titulo}</p>
                      {grupo.items.map(it => (
                        <button key={it.label} style={st.sugBtn} onClick={() => usarPlantilla(it.plantilla)}>{it.label}</button>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {visibles.map((m, i) => (
              <div key={i} style={{ ...st.burbuja, ...(m.role === 'user' ? st.burbujaUser : st.burbujaBot) }}>
                {m.texto.split('\n').map((linea, j) => <div key={j}>{linea || ' '}</div>)}
              </div>
            ))}

            {pendiente && !cargando && (
              <div style={st.confirmBar}>
                <Boton type="button" variante="success" onClick={() => resolver(true)}>✓ Confirmar</Boton>
                <Boton type="button" variante="ghost" onClick={() => resolver(false)}>Cancelar</Boton>
              </div>
            )}

            {cargando && (
              <div style={{ ...st.burbuja, ...st.burbujaBot, color: '#94a3b8' }}>
                <span style={st.typing}>● ● ●</span>
              </div>
            )}

            {error && <div style={st.errorBox}>{error}</div>}
          </div>

          {adjuntos.length > 0 && (
            <div style={st.adjBar}>
              {adjuntos.map((a, i) => (
                <span key={a.token} style={st.adjChip}>
                  📎 {a.nombre}
                  <button type="button" style={st.adjQuitar} title="Quitar"
                    onClick={() => setAdjuntos(prev => prev.filter((_, j) => j !== i))}>✕</button>
                </span>
              ))}
            </div>
          )}

          <form style={st.inputBar} onSubmit={e => { e.preventDefault(); enviar() }}>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => adjuntar(e.target.files?.[0])} />
            <button type="button" style={st.adjBtn} title="Adjuntar una captura de pantalla"
              disabled={cargando || subiendo || adjuntos.length >= 5}
              onClick={() => fileRef.current?.click()}>
              {subiendo ? '…' : '📎'}
            </button>
            <textarea
              ref={inputRef}
              style={st.input}
              value={input}
              onChange={e => {
                setInput(e.target.value)
                e.target.style.height = 'auto'
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  enviar()
                }
                // Shift+Enter: salto de línea (comportamiento por defecto del textarea)
              }}
              placeholder=""
              rows={1}
              disabled={cargando}
            />
            <button type="submit" style={st.sendBtn} disabled={cargando || (!input.trim() && adjuntos.length === 0)}>➤</button>
          </form>
        </div>
      )}
    </>
  )
}

const st = {
  fab: { position: 'fixed', bottom: 24, right: 24, width: 56, height: 56, borderRadius: '50%', background: '#1e40af', color: '#fff', border: 'none', boxShadow: '0 4px 16px rgba(30,64,175,0.4)', cursor: 'pointer', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  panel: { position: 'fixed', bottom: 24, right: 24, width: 380, maxWidth: 'calc(100vw - 32px)', height: 560, maxHeight: 'calc(100vh - 48px)', background: '#fff', borderRadius: 16, boxShadow: '0 12px 40px rgba(0,0,0,0.25)', zIndex: 1000, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  header: { background: '#1e40af', color: '#fff', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  headerTitle: { display: 'flex', alignItems: 'center', gap: 10 },
  headerName: { fontWeight: 700, fontSize: '0.9rem' },
  headerSub: { fontSize: '0.7rem', opacity: 0.85 },
  iconBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 28, height: 28, borderRadius: 6, cursor: 'pointer', fontSize: '0.9rem' },
  body: { flex: 1, overflowY: 'auto', padding: '14px', display: 'flex', flexDirection: 'column', gap: 10, background: '#f8fafc' },
  welcome: { textAlign: 'center', color: '#64748b', padding: '20px 8px', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' },
  welcomeText: { fontSize: '0.85rem', margin: 0, lineHeight: 1.5 },
  menu: { display: 'flex', flexDirection: 'column', gap: 12, width: '100%', textAlign: 'left' },
  menuGrupo: { display: 'flex', flexDirection: 'column', gap: 5 },
  menuTitulo: { fontSize: '0.68rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 2px 2px' },
  sugBtn: { padding: '8px 12px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', color: '#1e40af', fontWeight: 600, textAlign: 'left' },
  burbuja: { maxWidth: '85%', padding: '8px 12px', borderRadius: 12, fontSize: '0.85rem', lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  burbujaUser: { alignSelf: 'flex-end', background: '#1e40af', color: '#fff', borderBottomRightRadius: 4 },
  burbujaBot: { alignSelf: 'flex-start', background: '#fff', color: '#1e293b', border: '1px solid #e2e8f0', borderBottomLeftRadius: 4 },
  typing: { letterSpacing: 2, fontSize: '0.6rem' },
  confirmBar: { display: 'flex', gap: 8, alignSelf: 'flex-start' },
  errorBox: { background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 8, fontSize: '0.8rem', alignSelf: 'stretch' },
  inputBar: { display: 'flex', gap: 8, padding: 12, borderTop: '1px solid #e2e8f0', background: '#fff', alignItems: 'flex-end' },
  adjBar: { display: 'flex', gap: 6, flexWrap: 'wrap', padding: '8px 12px 0', background: '#fff', borderTop: '1px solid #e2e8f0' },
  adjChip: { display: 'inline-flex', alignItems: 'center', gap: 4, background: '#eff6ff', color: '#1e40af', borderRadius: 12, padding: '3px 8px', fontSize: '0.72rem', maxWidth: 220, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' },
  adjQuitar: { background: 'none', border: 'none', color: '#1e40af', cursor: 'pointer', fontSize: '0.72rem', padding: 0, lineHeight: 1 },
  adjBtn: { background: '#f1f5f9', border: '1px solid #d1d5db', borderRadius: 10, width: 38, height: 38, cursor: 'pointer', fontSize: '0.95rem', flexShrink: 0 },
  input: { flex: 1, padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 16, fontSize: '0.85rem', outline: 'none', resize: 'none', fontFamily: 'inherit', lineHeight: 1.4, maxHeight: 120, minHeight: 38, boxSizing: 'border-box' },
  sendBtn: { width: 38, height: 38, borderRadius: '50%', background: '#1e40af', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.9rem', flexShrink: 0 },
}
